/**
 * 契約一覧・契約詳細の表示の言葉と、取得・保存・削除に失敗したときの文言。
 */

import type { Category, FaceValue, Failure, PricingPlan } from '@/lib/api/credit-contracts';
import { faceValueAt, yenOf } from '@/lib/credit-contracts/validation';
import { formatNumber, parseNumberInput } from '@/lib/credit-conversion-settings/number';

/** 料金プランの表示（列挙型定義書の「日本語」） */
export const planLabels: Record<PricingPlan, string> = {
  Credit: 'クレジット制',
  FixedFee: '定額制',
};

export const categoryLabels: Record<Category, string> = {
  scheduled: '予定',
  active: '適用中',
  past: '過去',
};

/** データコネクトのクレジット数と、開始月の1日に有効な額面で円に直した額 */
export function creditWithYen(credit: string, start: string, faces: FaceValue[]): string {
  const face = faceValueAt(faces, start);
  const parsed = parseNumberInput(credit);
  const canonical = parsed.ok ? parsed.canonical : credit;
  const text = `${formatNumber(canonical)}クレジット`;
  if (face === null) return `${text}（開始月の1日に有効な1クレジットの額面がありません）`;
  return `${text}（${formatNumber(yenOf(canonical, face))}円相当）`;
}

/** データコネクトの一覧の相当額：開始月の1日に有効な額面で円に直した額。有効な額面がなければ null */
export function yenEquivalent(credit: string, start: string, faces: FaceValue[]): string | null {
  const face = faceValueAt(faces, start);
  if (face === null) return null;
  const parsed = parseNumberInput(credit);
  return formatNumber(yenOf(parsed.ok ? parsed.canonical : credit, face));
}

/** 数値の表示。null は空 */
export function numberText(value: string | null | undefined): string {
  return value ? formatNumber(value) : '';
}

const updatedAtFormat = new Intl.DateTimeFormat('ja-JP', {
  timeZone: 'Asia/Tokyo',
  year: 'numeric',
  month: '2-digit',
  day: '2-digit',
  hour: '2-digit',
  minute: '2-digit',
  hourCycle: 'h23',
});

/** 更新日時の表示（日本時間の `YYYY-MM-DD HH:mm`） */
export function formatUpdatedAt(iso: string): string {
  const parts = Object.fromEntries(
    updatedAtFormat.formatToParts(new Date(iso)).map((p) => [p.type, p.value]),
  );
  return `${parts.year}-${parts.month}-${parts.day} ${parts.hour}:${parts.minute}`;
}

/** 失敗の理由として括弧に入れる文。文末の「。」は、括弧の後に付ける「。」と重なるので外す */
function reasonOf(f: Failure): string {
  return f.message.replace(/。$/, '');
}

/** 取得に失敗したときの文言 */
export function fetchFailureMessage(f: Failure): string {
  switch (f.code) {
    case 'CREDIT_DB_UNAVAILABLE':
      return 'credit DBに接続できませんでした。少し待ってから、もう一度取得してください。';
    case 'COMMON_DB_UNAVAILABLE':
      return 'CommonDBから企業を取得できませんでした。少し待ってから、もう一度取得してください。';
    case 'NETWORK':
      return `サーバーに接続できませんでした（${reasonOf(f)}）。`;
    default:
      return `取得できませんでした（${reasonOf(f)}）。`;
  }
}

/**
 * 保存・削除に失敗したときの表示。
 * - `refetch`：「保証すること」の、バリデーションより先に判定する条件に当たった。保存・削除し直せず、契約の値を取得し直す
 * - `retry`：保存・削除を始める前に失敗した。保存・削除し直せる
 * - `unknown`：DBに反映できたかが分からない。入力を変えられず、同じ内容のままの再度の保存・削除か、破棄して取得し直すことだけができる
 */
export interface ActionFailure {
  kind: 'refetch' | 'retry' | 'unknown';
  message: string;
}

/** 保存・削除の操作。追加では、取得した後に変わりうる行は終了月を合わせる行だけ */
export type Operation = 'create' | 'update' | 'delete';

export function actionFailure(operation: Operation, f: Failure): ActionFailure {
  const verb = operation === 'delete' ? '削除' : '保存';
  const refetch = '「取得し直す」を押して、契約の値を取得し直してから、操作し直してください。';
  switch (f.code) {
    case 'ROW_CHANGED':
      return {
        kind: 'refetch',
        message: `契約の値を取得した後に、${operation === 'create' ? '終了月を合わせる行' : 'この行'}がほかの保存や削除で変わったか、なくなったため、${verb}しませんでした。${refetch}`,
      };
    case 'COMPANY_NOT_FOUND':
      return {
        kind: 'refetch',
        message: `この企業がCommonDBにないため、${verb}しませんでした。${refetch}`,
      };
    case 'MONTH_CHANGED':
      return {
        kind: 'refetch',
        message: `契約の値を取得した後に当月が変わったため、${verb}しませんでした。${refetch}`,
      };
    case 'NOT_ALLOWED':
      return {
        kind: 'refetch',
        message: `当月以前の月の値は変えられないため、${verb}しませんでした。${refetch}`,
      };
    case 'USER_EMAIL_MISSING':
      return {
        kind: 'retry',
        message: `操作した利用者のメールアドレスを取得できなかったため、${verb}しませんでした。`,
      };
    case 'CREDIT_DB_UNAVAILABLE':
      return {
        kind: 'retry',
        message: `credit DBに接続できなかったため、${verb}しませんでした。少し待ってから、もう一度${verb}してください。`,
      };
    case 'COMMON_DB_UNAVAILABLE':
      return {
        kind: 'retry',
        message: `CommonDBから企業を取得できなかったため、${verb}しませんでした。少し待ってから、もう一度${verb}してください。`,
      };
    case 'SAVE_BUSY':
      return {
        kind: 'retry',
        message: `同じ企業の同じテーブルへのほかの保存・削除が終わらなかったため、${verb}しませんでした。もう一度${verb}してください。`,
      };
    default:
      return {
        kind: 'unknown',
        message:
          verb === '保存'
            ? `保存できたかが分かりません（${reasonOf(f)}）。入力は変えられません。同じ内容のまま、もう一度保存するか、入力内容を破棄して契約の値を取得し直してください。保存できていた場合は、もう一度保存すると、追加では期間の重なりか行が変わった旨、編集では行が変わった旨のエラーになります。`
            : `削除できたかが分かりません（${reasonOf(f)}）。もう一度削除するか、契約の値を取得し直してください。削除できていた場合は、もう一度削除すると、行が変わった旨のエラーになります。`,
      };
  }
}
